import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trophy, ArrowLeft, Home, RotateCcw, Users, Award, Crown, Share2, Copy, CheckCircle, BarChart3, Target, Handshake, Flame } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useTournament } from "../context/TournamentContext";
import { useLanguage } from "../context/LanguageContext";
import { playerTotalsMap, computePlayerStats } from "../lib/playerStats";
import { StatLeaderList } from "./PlayerStatsPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import ExportButton from "../components/ExportButton";
import LanguageSelector from "../components/LanguageSelector";

const CONFETTI_COLORS = ["#00F0FF", "#39FF14", "#7000FF", "#FFD600", "#FF003C"];

function Confetti() {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 2,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 8 + Math.random() * 8,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-10">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute confetti"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            animation: `confetti-fall ${p.duration}s linear ${p.delay}s forwards`,
          }}
        />
      ))}
    </div>
  );
}

export default function ChampionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTournament, loadTournament } = useTournament();
  const { t } = useLanguage();
  const championRef = useRef(null);

  // Share functionality
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Generate share link
  const shareLink = typeof window !== 'undefined' ? `${window.location.origin}/t/${id}` : '';

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      toast.success(t('toast.linkCopied'));
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error(t('toast.linkCopyFailed'));
    }
  };

  useEffect(() => {
    if (id) {
      loadTournament(id);
    }
  }, [id, loadTournament]);

  if (!currentTournament || !currentTournament.champion) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">{t('loading')}</p>
          <Button onClick={() => navigate("/")} className="bg-neon-cyan text-black">
            {t('champion.newTournament')}
          </Button>
        </div>
      </div>
    );
  }

  const { champion, name, sportType, participants, format, standings, matches, losersMatches, grandFinal, hasLosersLeague, hasPlayerStats } = currentTournament;
  const championTeam = (participants || []).find((p) => p.id === champion.id) || (participants || []).find((p) => p.name === champion.name);
  const championPlayers = Array.isArray(championTeam?.players) ? championTeam.players.filter((p) => (p?.name || '').trim()) : [];
  const statTotals = hasPlayerStats ? playerTotalsMap(currentTournament) : {};
  const playerStatsData = hasPlayerStats ? computePlayerStats(currentTournament) : { hasAny: false };

  // Calculate bracket standings with proper points
  const calculateBracketRankings = () => {
    if (!participants || participants.length === 0) return [];
    
    const rankings = participants.filter(p => !p.isBye).map(p => ({
      ...p,
      rank: 0,
      points: 0,
      wins: 0,
      losses: 0,
    }));
    
    // Process all matches to calculate wins/losses
    const allMatches = [...(matches || []), ...(losersMatches || [])];
    
    allMatches.forEach(match => {
      if (match.status === 'completed' && match.winner && match.loser && !match.loser.isBye) {
        const winner = rankings.find(p => p.id === match.winner.id);
        const loser = rankings.find(p => p.id === match.loser.id);
        
        if (winner) {
          winner.wins++;
          winner.points += 3; // 3 points per win
        }
        if (loser) {
          loser.losses++;
        }
      }
    });
    
    // Process Grand Final if exists
    if (grandFinal?.status === 'completed') {
      const winner = rankings.find(p => p.id === grandFinal.winner?.id);
      const loser = rankings.find(p => p.id === grandFinal.loser?.id);
      if (winner) {
        winner.wins++;
        winner.points += 5; // Bonus for Grand Final win
      }
      if (loser) {
        loser.losses++;
      }
    }
    
    // Double elimination: rank by losses (asc) then wins (desc); points break ties.
    // Other bracket formats: rank by points (desc), then wins (desc), then losses (asc).
    const isDoubleElimination = hasLosersLeague && losersMatches?.length > 0;
    rankings.sort((a, b) => {
      if (isDoubleElimination) {
        if (a.losses !== b.losses) return a.losses - b.losses;
        if (b.wins !== a.wins) return b.wins - a.wins;
        return b.points - a.points;
      }
      if (b.points !== a.points) return b.points - a.points;
      if (b.wins !== a.wins) return b.wins - a.wins;
      return a.losses - b.losses;
    });
    
    // Assign ranks
    rankings.forEach((p, idx) => {
      p.rank = idx + 1;
    });
    
    return rankings;
  };

  // Get final standings based on format
  const sortedParticipants = format === "league" && standings
    ? standings
    : calculateBracketRankings();

  const handleViewTournament = () => {
    if (format === "league") {
      navigate(`/league/${id}`);
    } else {
      navigate(`/bracket/${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      <Confetti />

      {/* Radial glow */}
      <div className="absolute inset-0 bg-gradient-radial from-neon-cyan/10 via-transparent to-transparent opacity-50" />
      <div className="absolute inset-0 bg-gradient-radial from-neon-green/10 via-transparent to-transparent opacity-30 translate-y-1/2" />

      {/* Header */}
      <header className="relative z-20 border-b border-white/10 bg-black/50 backdrop-blur-sm">
        <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                data-testid="back-btn"
                variant="ghost"
                size="icon"
                onClick={handleViewTournament}
                className="text-white/60 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-heading text-base sm:text-xl font-bold tracking-tight text-white uppercase">
                  {name}
                </h1>
                <p className="text-white/40 text-xs sm:text-sm font-data">{sportType}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowShareDialog(true)}
                className="border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan/10 gap-2"
                data-testid="share-tournament-btn"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">{t('share')}</span>
              </Button>
              <ExportButton 
                targetRef={championRef} 
                filename={`${name}-champion`}
                title={t('export')}
              />
            </div>
          </div>
        </div>
      </header>

      <main ref={championRef} className="relative z-20 container mx-auto px-3 sm:px-6 py-6 sm:py-12">
        {/* Champion Announcement */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-neon-green/10 border border-neon-green/30 px-4 py-2 mb-8">
            <Award className="w-4 h-4 text-neon-green" />
            <span className="font-heading uppercase tracking-widest text-neon-green text-sm">
              {t('champion.tournamentComplete')}
            </span>
          </div>

          <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-neon-cyan to-neon-green rounded-full flex items-center justify-center shadow-neon trophy-animation">
            <Trophy className="w-16 h-16 text-black" />
          </div>

          <h2 className="font-heading text-2xl uppercase tracking-widest text-white/60 mb-4">
            {t('bracket.champion')}
          </h2>

          {/* Champion Logo */}
          {champion.logo && (
            <div className="mb-6">
              <img 
                src={champion.logo} 
                alt={champion.name} 
                className="w-24 h-24 rounded-lg object-cover mx-auto border-2 border-neon-green/50 shadow-lg"
              />
            </div>
          )}

          <h1
            className="font-heading text-6xl md:text-8xl font-black uppercase tracking-tight text-gradient glow-text mb-8"
            data-testid="champion-name"
          >
            {champion.name}
          </h1>

          {format === "league" ? (
            <p className="text-white/40 font-data text-lg">
              {champion.points} {t('bracket.points')} • {champion.wins}{t('league.won')} - {champion.draws}{t('league.drawn')} - {champion.losses}{t('league.lost')}
            </p>
          ) : (
            <p className="text-white/40 font-data text-lg">
              {sortedParticipants[0]?.wins || 0} {t('bracket.wins')} • {sortedParticipants[0]?.losses || 0} {t('bracket.losses')} • {sortedParticipants[0]?.points || 0} {t('bracket.points')}
            </p>
          )}

          {/* Champion roster */}
          {championPlayers.length > 0 && (
            <div className="mt-6 max-w-md mx-auto" data-testid="champion-roster">
              <p className="text-white/40 text-xs uppercase tracking-widest mb-3">{t('champion.roster')}</p>
              <div className="flex flex-wrap justify-center gap-2">
                {championPlayers.map((pl) => {
                  const tot = statTotals[pl.id];
                  return (
                    <span
                      key={pl.id}
                      data-testid={`champion-player-${pl.id}`}
                      className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5"
                    >
                      <span className="text-white font-data text-sm">{pl.name}</span>
                      {hasPlayerStats && tot && (tot.goals > 0 || tot.assists > 0) && (
                        <span className="text-xs font-data text-white/50">
                          <span className="text-neon-cyan">{tot.goals}{t('stats.goalsAbbr')}</span>
                          {tot.assists > 0 && <span className="text-neon-purple ml-1">{tot.assists}{t('stats.assistsAbbr')}</span>}
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Player Statistics */}
        {hasPlayerStats && playerStatsData.hasAny && (
          <div className="max-w-5xl mx-auto mb-10" data-testid="champion-player-stats">
            <div className="flex items-center gap-2 mb-6 justify-center">
              <BarChart3 className="w-5 h-5 text-neon-purple" />
              <h3 className="font-heading text-xl uppercase tracking-wide text-white/80">
                {t('stats.playerStats')}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatLeaderList
                title={t('stats.topScorers')}
                icon={Target}
                players={playerStatsData.scorers}
                metric="goals"
                color="text-neon-cyan"
                testid="champion-top-scorers"
              />
              <StatLeaderList
                title={t('stats.topAssisters')}
                icon={Handshake}
                players={playerStatsData.assisters}
                metric="assists"
                color="text-neon-purple"
                testid="champion-top-assisters"
              />
              <StatLeaderList
                title={t('stats.topGA')}
                subtitle={t('stats.topGADesc')}
                icon={Flame}
                players={playerStatsData.combined}
                metric="ga"
                color="text-orange-400"
                testid="champion-top-ga"
              />
            </div>
          </div>
        )}

        {/* Final Standings */}
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-white/40" />
              <h3 className="font-heading text-xl uppercase tracking-wide text-white/80">
                {t('bracket.finalStandings')}
              </h3>
            </div>
            <Badge className="bg-neon-green/20 text-neon-green">{t('champion.tournamentComplete')}</Badge>
          </div>

          <div className="card-surface overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-white/40 text-sm font-heading uppercase">
                    {t('bracket.rank')}
                  </th>
                  <th className="text-left py-3 px-4 text-white/40 text-sm font-heading uppercase">
                    {format === "league" ? t('bracket.player') : t('bracket.player')}
                  </th>
                  {format === "league" ? (
                    <>
                      <th className="text-center py-3 px-4 text-white/40 text-sm font-heading uppercase">
                        {t('bracket.points')}
                      </th>
                      <th className="text-center py-3 px-4 text-white/40 text-sm font-heading uppercase">
                        {t('league.goalDiff')}
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="text-center py-3 px-4 text-white/40 text-sm font-heading uppercase">
                        {t('bracket.wins')}
                      </th>
                      <th className="text-center py-3 px-4 text-white/40 text-sm font-heading uppercase">
                        {t('bracket.losses')}
                      </th>
                      <th className="text-center py-3 px-4 text-white/40 text-sm font-heading uppercase">
                        {t('bracket.points')}
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedParticipants.slice(0, 10).map((participant, index) => (
                  <tr
                    key={participant.id}
                    className={`border-b border-white/5 ${
                      index === 0 ? "bg-neon-green/10" : 
                      index === 1 ? "bg-neon-cyan/5" : 
                      index === 2 ? "bg-orange-500/5" : ""
                    }`}
                    data-testid={`standing-row-${participant.id}`}
                  >
                    <td className="py-3 px-4">
                      <div className={`flex items-center justify-center w-7 h-7 rounded-full font-heading font-bold ${
                        index === 0 ? 'bg-neon-green text-black' :
                        index === 1 ? 'bg-neon-cyan text-black' :
                        index === 2 ? 'bg-orange-500 text-black' :
                        'bg-white/10 text-white/60'
                      }`}>
                        {index + 1}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {participant.logo && (
                          <img src={participant.logo} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                        )}
                        <span
                          className={`font-heading uppercase ${
                            index === 0 ? "text-neon-green font-bold" : "text-white"
                          }`}
                        >
                          {participant.name}
                        </span>
                        {index === 0 && <Crown className="w-4 h-4 text-neon-green" />}
                      </div>
                    </td>
                    {format === "league" ? (
                      <>
                        <td className="py-3 px-4 text-center">
                          <span className="font-data font-bold text-white">{participant.points}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`font-data ${participant.goalDiff > 0 ? "text-neon-green" : participant.goalDiff < 0 ? "text-red-400" : "text-white/60"}`}>
                            {participant.goalDiff > 0 ? "+" : ""}{participant.goalDiff}
                          </span>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-3 px-4 text-center">
                          <span className="font-data text-neon-green">{participant.wins || 0}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-data text-red-400">{participant.losses || 0}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-data font-bold text-white">{participant.points || 0}</span>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-12">
          <Button
            data-testid="view-tournament-btn"
            variant="ghost"
            onClick={handleViewTournament}
            className="text-white/60 hover:text-white border border-white/20 hover:border-white/40"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {format === "league" ? t('champion.viewLeague') : t('champion.viewBracket')}
          </Button>
          <Button
            data-testid="new-tournament-btn"
            onClick={() => navigate("/")}
            className="btn-skewed bg-neon-cyan text-black font-heading uppercase tracking-wide hover:bg-neon-cyan/90"
          >
            <span className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              {t('champion.newTournament')}
            </span>
          </Button>
        </div>
      </main>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-surface-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-neon-cyan" />
              {t('shareDialog.title')}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {t('shareDialog.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-white/60 text-sm font-heading uppercase">{t('shareDialog.shareLink')}</label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={shareLink}
                  className="input-dark font-data text-sm"
                  data-testid="share-link-input"
                />
                <Button
                  onClick={copyShareLink}
                  className={`px-4 ${linkCopied ? 'bg-neon-green text-black' : 'bg-neon-cyan text-black'} hover:opacity-90`}
                  data-testid="copy-link-btn"
                >
                  {linkCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex justify-center py-4">
              <div className="bg-white p-3 rounded-lg">
                <QRCodeSVG value={shareLink} size={150} level="M" data-testid="share-qr-code" />
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <p className="text-white/40 text-xs">
                {t('shareDialog.orShare')}
              </p>
              <div className="flex gap-4 mt-2">
                <div>
                  <span className="text-white/60 text-xs">{t('shareDialog.name')}:</span>
                  <p className="text-white font-heading uppercase">{currentTournament.name}</p>
                </div>
                <div>
                  <span className="text-white/60 text-xs">{t('shareDialog.code')}:</span>
                  <p className="text-neon-cyan font-data font-bold">{currentTournament.code}</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowShareDialog(false)}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
